import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { OverviewFilters } from "@nexus/contracts";
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
} from "@nexus/ui";
import { useTranslation } from "react-i18next";
import type { CompetitorFiltersInput } from "@/lib/queries/dashboard.queries";
import {
  buildCompTopicDetailFatigueSearch,
  compTopicDetailFatigueQueryOptions,
  overviewKeys,
} from "@/lib/queries/overview.queries";
import { useTopicDetailAsyncResource } from "../../hooks/useTopicDetailAsyncResource.js";
import { formatMetricValue } from "@/features/dashboard/lib/format";
import type { CompTopicDetailContext } from "../../lib/compTopicDetailContext.js";
import {
  COMP_TOPIC_DETAIL_FATIGUE_KPIS,
  type CompTopicDetailFatigueKpi,
} from "../../lib/compTopicDetailKpis.js";
import {
  compFatigueKpiDisplayFormat,
  compFatigueValueForKpi,
} from "../../lib/compFatigueKpiValue.js";
import { useKpiSelectorCatalog } from "@/features/kpi-settings/hooks/useKpiSelectorCatalog";

const AXIS_TICK_FORMAT = new Intl.DateTimeFormat(undefined, {
  month: "short",
  day: "numeric",
  timeZone: "UTC",
});

/** Epoch (ms, UTC) → compact `MMM d` label for the time-scaled fatigue axis. */
function formatWeekTick(ts: number): string {
  return Number.isFinite(ts) ? AXIS_TICK_FORMAT.format(new Date(ts)) : "";
}

/** Epoch (ms, UTC) → full `YYYY-MM-DD` for the tooltip (keeps the year). */
function formatWeekLabel(ts: number): string {
  return Number.isFinite(ts) ? new Date(ts).toISOString().slice(0, 10) : "";
}

interface CompTopicDetailFatigueTabProps {
  tenantId: string;
  context: CompTopicDetailContext;
  overviewFilters: OverviewFilters;
  competitorFilters: CompetitorFiltersInput;
}

export function CompTopicDetailFatigueTab({
  tenantId,
  context,
  overviewFilters,
  competitorFilters,
}: CompTopicDetailFatigueTabProps) {
  const { t } = useTranslation();
  const { metricById } = useKpiSelectorCatalog("overview");
  const [selectedKpi, setSelectedKpi] = useState<CompTopicDetailFatigueKpi>("Days");

  const querySearch = buildCompTopicDetailFatigueSearch(
    overviewFilters,
    competitorFilters,
    context,
  );

  const { data: summary } = useQuery(
    compTopicDetailFatigueQueryOptions(
      tenantId,
      overviewFilters,
      competitorFilters,
      context,
    ),
  );

  const resetKey = `${tenantId}:${querySearch}`;
  const { data, isLoading, isError } = useTopicDetailAsyncResource({
    summary: summary ?? undefined,
    targetKey: overviewKeys.compTopicDetailFatigue(
      tenantId,
      overviewFilters,
      competitorFilters,
      context,
    ),
    resetKey,
    enabled: !!tenantId && !!context,
  });

  const valueFormat = useMemo(
    () => compFatigueKpiDisplayFormat(selectedKpi),
    [selectedKpi],
  );

  const formatValue = useMemo(
    () => (value: number) => formatMetricValue(value, valueFormat),
    [valueFormat],
  );

  const chartData = useMemo(
    () =>
      (data ?? []).map((point) => ({
        // Numeric epoch (UTC) so the axis is time-scaled — points sit at their real
        // date and gaps/missing weeks are spaced proportionally, not evenly by index.
        ts: Date.parse(`${point.periodStart}T00:00:00Z`),
        value: compFatigueValueForKpi(point, selectedKpi),
      })),
    [data, selectedKpi],
  );
  const seriesColor = metricById[selectedKpi]?.colorVar ?? "var(--primary)";

  const chartContent = (() => {
    if (isLoading) {
      return <Skeleton className="h-[320px] w-full rounded-lg" />;
    }
    if (isError) {
      return (
        <p className="text-sm text-destructive">
          {t("overview.topicDetail.fatigueError", { defaultValue: "Failed to load fatigue data." })}
        </p>
      );
    }
    if (chartData.length === 0) {
      return (
        <p className="text-sm text-muted-foreground">
          {t("overview.topicDetail.noFatigueData", { defaultValue: "No fatigue data for this topic." })}
        </p>
      );
    }
    return (
      <div className="h-[320px] w-full">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 8, right: 16, bottom: 8, left: 8 }}>
            <defs>
              <linearGradient id="compFatigueLineGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={seriesColor} stopOpacity={0.3} />
                <stop offset="100%" stopColor={seriesColor} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false} />
            <XAxis
              dataKey="ts"
              type="number"
              scale="time"
              domain={["dataMin", "dataMax"]}
              tickFormatter={formatWeekTick}
              minTickGap={48}
              interval="preserveStartEnd"
              tick={{ fontSize: 11, fill: "var(--muted-foreground)" }}
              tickLine={false}
              axisLine={false}
            />
            <YAxis
              tick={{ fontSize: 11, fill: "var(--muted-foreground)" }}
              tickLine={false}
              axisLine={false}
              width={56}
              tickFormatter={formatValue}
            />
            <Tooltip
              contentStyle={{
                background: "var(--card)",
                border: "1px solid var(--border)",
                borderRadius: 8,
                fontSize: 12,
              }}
              labelFormatter={(label) => formatWeekLabel(typeof label === "number" ? label : Number(label))}
              formatter={(value) => formatValue(typeof value === "number" ? value : 0)}
            />
            <Area
              type="monotone"
              dataKey="value"
              name={selectedKpi}
              stroke={seriesColor}
              strokeWidth={2}
              fill="url(#compFatigueLineGradient)"
              dot={false}
              activeDot={{ r: 4 }}
              isAnimationActive
              animationDuration={900}
              animationEasing="ease-out"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    );
  })();

  return (
    <div className="flex flex-col gap-4 px-6 py-5">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {t("overview.topicDetail.fatigueTitle", { defaultValue: "Fatigue over time" })}
        </p>
        <Select
          value={selectedKpi}
          onValueChange={(value) => setSelectedKpi(value as CompTopicDetailFatigueKpi)}
        >
          <SelectTrigger className="h-8 w-[160px] text-xs">
            <SelectValue placeholder={t("overview.topicDetail.selectKpi", { defaultValue: "Select KPI" })} />
          </SelectTrigger>
          <SelectContent>
            {COMP_TOPIC_DETAIL_FATIGUE_KPIS.map((kpi) => (
              <SelectItem key={kpi} value={kpi}>
                {kpi}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {chartContent}
    </div>
  );
}
